// components/CombinedHourlyGraph.jsx  (React web — Highcharts)
import React, { useMemo, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';

// ─── Constants ────────────────────────────────────────────────────────────────
const TIME_RANGES = [
  { key: 'hourly',  label: 'Hourly'  },
  { key: 'daily',   label: 'Daily'   },
  { key: 'monthly', label: 'Monthly' },
];

const UNIT_OPTIONS = [
  { key: 'MB', divisor: 1        },
  { key: 'GB', divisor: 1024     },
  { key: 'TB', divisor: 1048576  },
];

// Color palette per WAN — index matches WAN order
const WAN_COLORS = [
  { download: '#2196F3', upload: '#64B5F6' },
  { download: '#E91E63', upload: '#F48FB1' },
  { download: '#FF9800', upload: '#FFCC80' },
  { download: '#9C27B0', upload: '#CE93D8' },
  { download: '#009688', upload: '#80CBC4' },
  { download: '#F44336', upload: '#EF9A9A' },
];

const VIEW_TOTAL  = 'total';
const VIEW_SPLIT  = 'split';
const VIEW_SINGLE = 'single';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const parseTsToMs = (ts) => {
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d.getTime();
};

const formatXLabel = (ms, range) => {
  const dt = DateTime.fromMillis(ms).toLocal();
  if (range === 'hourly')  return dt.toFormat('HH:mm');
  if (range === 'daily')   return dt.toFormat('dd/MM');
  if (range === 'monthly') return dt.toFormat('MMM yy');
  return dt.toFormat('HH:mm');
};

// ─── Main component ───────────────────────────────────────────────────────────
const CombinedHourlyGraph = ({
  hourlyWanUsagesLogs,
  dailyWanUsagesLogs,
  monthlyWanUsagesLogs,
}) => {
  const [timeRange,     setTimeRange]     = useState('hourly');
  const [unit,          setUnit]          = useState('GB');
  const [viewMode,      setViewMode]      = useState(VIEW_TOTAL);
  const [selectedWanId, setSelectedWanId] = useState(null);
  const [hiddenWans,    setHiddenWans]    = useState(new Set());

  // ── Raw data for current time range ───────────────────────────────────────
  const rawData = useMemo(() => {
    if (timeRange === 'hourly')  return hourlyWanUsagesLogs  ?? null;
    if (timeRange === 'daily')   return dailyWanUsagesLogs   ?? null;
    if (timeRange === 'monthly') return monthlyWanUsagesLogs ?? null;
    return null;
  }, [timeRange, hourlyWanUsagesLogs, dailyWanUsagesLogs, monthlyWanUsagesLogs]);

  // ── Parse structure ────────────────────────────────────────────────────────
  const parsed = useMemo(() => {
    if (!rawData) return null;
    const usages  = Array.isArray(rawData.usages)  ? rawData.usages  : [];
    const details = rawData.details ?? {};
    const wans    = (rawData.wans ?? []).filter(w => w.id !== 0);
    const timestamps = usages.map(u => parseTsToMs(u.ts)).filter(Boolean);
    return { timestamps, usages, details, wans };
  }, [rawData]);

  // ── Unit helpers ───────────────────────────────────────────────────────────
  const unitDivisor = UNIT_OPTIONS.find(u => u.key === unit)?.divisor ?? 1024;
  const convert = (mb) => (mb ?? 0) / unitDivisor;
  const fmt = (val) => {
    const n = val ?? 0;
    return `${n < 10 ? n.toFixed(2) : n.toFixed(1)} ${unit}`;
  };

  // ── Build Highcharts series + totals ───────────────────────────────────────
  const { series, totals, wans } = useMemo(() => {
    if (!parsed || parsed.timestamps.length === 0) {
      return { series: [], totals: null, wans: [] };
    }

    const { timestamps, usages, details, wans } = parsed;

    // Helper: align a per-WAN series array to master timestamps
    const alignSeries = (wanIdStr) => {
      const wanSeries = details[wanIdStr] ?? [];
      return timestamps.map(ts => {
        const entry = wanSeries.find(e => parseTsToMs(e.ts) === ts);
        return { ts, down: entry?.down ?? 0, up: entry?.up ?? 0 };
      });
    };

    // ── TOTAL ──────────────────────────────────────────────────────────────
    if (viewMode === VIEW_TOTAL) {
      const dlPoints    = usages.map(u => [parseTsToMs(u.ts), convert(u.down)]);
      const ulPoints    = usages.map(u => [parseTsToMs(u.ts), convert(u.up)]);
      const totalPoints = usages.map(u => [parseTsToMs(u.ts), convert(u.down + u.up)]);

      const sumDl = usages.reduce((s, u) => s + convert(u.down), 0);
      const sumUl = usages.reduce((s, u) => s + convert(u.up),   0);

      return {
        wans,
        totals: { dl: sumDl, ul: sumUl, total: sumDl + sumUl },
        series: [
          {
            name: 'Download',
            data: dlPoints,
            color: 'var(--color-download)',
            dashStyle: 'Solid',
            lineWidth: 2,
          },
          {
            name: 'Upload',
            data: ulPoints,
            color: 'var(--color-upload)',
            dashStyle: 'Solid',
            lineWidth: 2,
          },
          {
            name: 'Total',
            data: totalPoints,
            color: 'var(--color-accent)',
            dashStyle: 'Dash',
            lineWidth: 2,
          },
        ],
      };
    }

    // ── SINGLE WAN ─────────────────────────────────────────────────────────
    if (viewMode === VIEW_SINGLE && selectedWanId !== null) {
      const aligned  = alignSeries(String(selectedWanId));
      const wanIdx   = wans.findIndex(w => w.id === selectedWanId);
      const colors   = WAN_COLORS[Math.max(0, wanIdx) % WAN_COLORS.length];

      const dlPoints    = aligned.map(r => [r.ts, convert(r.down)]);
      const ulPoints    = aligned.map(r => [r.ts, convert(r.up)]);
      const totalPoints = aligned.map(r => [r.ts, convert(r.down + r.up)]);

      const sumDl = aligned.reduce((s, r) => s + convert(r.down), 0);
      const sumUl = aligned.reduce((s, r) => s + convert(r.up),   0);

      return {
        wans,
        totals: { dl: sumDl, ul: sumUl, total: sumDl + sumUl },
        series: [
          { name: 'Download', data: dlPoints,    color: colors.download, dashStyle: 'Solid', lineWidth: 2 },
          { name: 'Upload',   data: ulPoints,    color: colors.upload,   dashStyle: 'Solid', lineWidth: 2 },
          { name: 'Total',    data: totalPoints, color: 'var(--color-accent)', dashStyle: 'Dash', lineWidth: 2 },
        ],
      };
    }

    // ── SPLIT ──────────────────────────────────────────────────────────────
    if (viewMode === VIEW_SPLIT) {
      const result = [];
      let sumDl = 0, sumUl = 0;

      wans.forEach((wan, idx) => {
        if (hiddenWans.has(wan.id)) return;
        const aligned = alignSeries(String(wan.id));
        const colors  = WAN_COLORS[idx % WAN_COLORS.length];

        const dlPoints = aligned.map(r => [r.ts, convert(r.down)]);
        const ulPoints = aligned.map(r => [r.ts, convert(r.up)]);

        sumDl += aligned.reduce((s, r) => s + convert(r.down), 0);
        sumUl += aligned.reduce((s, r) => s + convert(r.up),   0);

        result.push({
          name: `${wan.name} ↓`,
          data: dlPoints,
          color: colors.download,
          dashStyle: 'Solid',
          lineWidth: 2,
          wanId: wan.id,
        });
        result.push({
          name: `${wan.name} ↑`,
          data: ulPoints,
          color: colors.upload,
          dashStyle: 'ShortDash',
          lineWidth: 1.5,
          wanId: wan.id,
        });
      });

      return {
        wans,
        totals: { dl: sumDl, ul: sumUl, total: sumDl + sumUl },
        series: result,
      };
    }

    return { series: [], totals: null, wans };
  }, [parsed, viewMode, selectedWanId, hiddenWans, unit]);

  // ── Toggle WAN visibility ──────────────────────────────────────────────────
  const toggleWan = (wanId) => {
    setHiddenWans(prev => {
      const next = new Set(prev);
      next.has(wanId) ? next.delete(wanId) : next.add(wanId);
      return next;
    });
  };

  // ── Highcharts options ─────────────────────────────────────────────────────
  const options = useMemo(() => ({
    chart: {
      type: 'spline',
      backgroundColor: 'transparent',
      height: 260,
      spacing: [10, 10, 15, 10],
    },
    title: { text: null },
    xAxis: {
      type: 'datetime',
      labels: {
        style: { color: 'var(--color-label)', fontSize: '9px' },
        formatter: function () {
          return formatXLabel(this.value, timeRange);
        },
      },
      gridLineWidth: 0,
      lineColor: 'var(--color-surface)',
      tickColor: 'var(--color-surface)',
    },
    yAxis: {
      title: {
        text: unit,
        style: { color: 'var(--color-label)', fontSize: '10px' },
      },
      labels: {
        style: { color: 'var(--color-label)', fontSize: '9px' },
        formatter: function () { return this.value.toFixed(1); },
      },
      gridLineColor: 'rgba(255,255,255,0.07)',
      min: 0,
    },
    legend: {
      // Disable built-in legend — we render our own for SPLIT mode chip toggle
      enabled: viewMode !== VIEW_SPLIT,
      itemStyle: { fontSize: '10px', color: 'var(--color-label)' },
      itemHoverStyle: { color: 'var(--color-primary)' },
    },
    tooltip: {
      shared: true,
      useHTML: true,
      backgroundColor: 'var(--navy-800, #112023)',
      borderColor: 'rgba(136,244,235,0.3)',
      borderWidth: 1,
      borderRadius: 0,
      shadow: { color: 'rgba(0,0,0,0.4)', offsetX: 0, offsetY: 4, opacity: 1, width: 12 },
      style: { fontSize: '11px' },
      formatter: function () {
        const localTime = formatXLabel(this.x, timeRange);
        let html = `<div style="font-size:10px;font-weight:600;color:var(--color-label);margin-bottom:5px">${localTime}</div>`;
        this.points.forEach(pt => {
          html += `
            <div style="display:flex;align-items:center;justify-content:space-between;margin-top:3px;gap:12px">
              <div style="display:flex;align-items:center;gap:5px">
                <span style="color:${pt.series.color};font-size:13px">●</span>
                <span style="color:var(--color-label)">${pt.series.name}</span>
              </div>
              <span style="font-weight:700;color:${pt.series.color}">${fmt(pt.y)}</span>
            </div>`;
        });
        return html;
      },
    },
    plotOptions: {
      series: {
        marker: { enabled: false },
        states: { hover: { halo: { size: 0 } } },
        animation: false,
      },
    },
    series,
    credits: { enabled: false },
  }), [series, unit, timeRange, viewMode]);

  // ── Derived ────────────────────────────────────────────────────────────────
  const getSelectedLabel = () => {
    if (viewMode === VIEW_SPLIT)  return 'All WANs (Individual)';
    if (viewMode === VIEW_TOTAL)  return 'All WANs (Total)';
    const wan = wans?.find(w => w.id === selectedWanId);
    return wan ? wan.name : 'Select WAN';
  };

  const hasData = parsed && parsed.timestamps.length > 0;

  if (!hasData) {
    return (
      <div className="combined-hourly-graph" style={styles.card}>
        <div style={styles.empty}>
          <p style={styles.muted}>No traffic data available</p>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="combined-hourly-graph" style={styles.card}>

      {/* Controls - Responsive layout */}
      <div style={styles.controlsContainer}>
        {/* First row: Time range + Unit tabs on mobile */}
        <div style={styles.topControlsRow}>
          {/* Time range tabs */}
          <div style={styles.tabGroup}>
            {TIME_RANGES.map(tr => (
              <button
                key={tr.key}
                style={{ 
                  ...styles.tab, 
                  ...styles.tabMobile,
                  ...(timeRange === tr.key ? styles.tabActive : {})
                }}
                onClick={() => setTimeRange(tr.key)}
              >
                {tr.label}
              </button>
            ))}
          </div>

          {/* Unit tabs */}
          <div style={styles.tabGroup}>
            {UNIT_OPTIONS.map(u => (
              <button
                key={u.key}
                style={{ 
                  ...styles.tab, 
                  ...styles.tabMobile,
                  ...(unit === u.key ? styles.tabActive : {})
                }}
                onClick={() => setUnit(u.key)}
              >
                {u.key}
              </button>
            ))}
          </div>
        </div>

        {/* Second row: WAN selector dropdown on mobile */}
        {wans && wans.length > 0 && (
          <div style={styles.selectWrapper}>
            <select
              value={viewMode === VIEW_SPLIT ? '__split__' : viewMode === VIEW_TOTAL ? '__total__' : String(selectedWanId)}
              onChange={e => {
                const v = e.target.value;
                if (v === '__total__') { setViewMode(VIEW_TOTAL); setSelectedWanId(null); }
                else if (v === '__split__') { setViewMode(VIEW_SPLIT); setSelectedWanId(null); }
                else { setViewMode(VIEW_SINGLE); setSelectedWanId(Number(v)); }
              }}
              style={styles.select}
            >
              <option value="__total__">All WANs (Total)</option>
              <option value="__split__">All WANs (Individual)</option>
              {wans.map(w => (
                <option key={w.id} value={String(w.id)}>{w.name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary totals */}
      {totals && (
        <div style={styles.totals}>
          <span style={styles.totalItem}>↓ {fmt(totals.dl)}</span>
          <span style={styles.totalDivider}>|</span>
          <span style={styles.totalItem}>↑ {fmt(totals.ul)}</span>
          <span style={styles.totalDivider}>|</span>
          <span style={styles.totalItem}>⇅ {fmt(totals.total)}</span>
        </div>
      )}

      {/* Chart */}
      <HighchartsReact highcharts={Highcharts} options={options} />

      {/* WAN chips — only in SPLIT mode */}
      {viewMode === VIEW_SPLIT && wans && wans.length > 0 && (
        <div style={styles.chipsRow}>
          {wans.map((wan, idx) => {
            const colors = WAN_COLORS[idx % WAN_COLORS.length];
            const hidden = hiddenWans.has(wan.id);
            return (
              <button
                key={wan.id}
                onClick={() => toggleWan(wan.id)}
                style={{
                  ...styles.chip,
                  ...(hidden ? styles.chipHidden : {}),
                }}
                title={hidden ? `Show ${wan.name}` : `Hide ${wan.name}`}
              >
                <span style={{
                  ...styles.chipSwatch,
                  background: hidden ? 'rgba(255,255,255,0.2)' : colors.download,
                }} />
                <span style={{
                  ...styles.chipSwatch,
                  background: hidden ? 'rgba(255,255,255,0.15)' : colors.upload,
                  marginLeft: 2,
                }} />
                <span style={{
                  ...styles.chipLabel,
                  ...(hidden ? styles.chipLabelHidden : {}),
                }}>
                  {wan.name}
                </span>
                {hidden && <span style={{ marginLeft: 4, fontSize: 10, opacity: 0.5 }}>👁</span>}
              </button>
            );
          })}
          <span style={styles.chipHint}>CLICK CHIP TO HIDE/SHOW</span>
        </div>
      )}

    </div>
  );
};

// ─── Inline styles (no CSS file dependency) ───────────────────────────────────
const styles = {
  card: {
    padding: 15,
    paddingBottom: 0,
  },
  controlsContainer: {
    marginBottom: 12,
  },
  topControlsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    marginBottom: 10,
  },
  controlsRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
    marginBottom: 12,
  },
  tabGroup: {
    display: 'flex',
    border: '1px solid rgba(255,255,255,0.16)',
    background: 'var(--color-card)',
  },
  tab: {
    padding: '6px 13px',
    fontSize: 10.5,
    fontFamily: "'DM Mono', monospace",
    letterSpacing: '0.06em',
    color: 'var(--color-label)',
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    transition: 'background 0.15s',
  },
  tabMobile: {
    padding: '5px 10px',
    fontSize: 10,
  },
  tabActive: {
    background: 'var(--turquoise, #88F4EB)',
    color: 'var(--navy-800, #112023)',
  },
  selectWrapper: {
    width: '100%',
  },
  select: {
    width: '100%',
    padding: '7px 12px',
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
    borderRadius: 0,
    border: '1px solid rgba(255,255,255,0.16)',
    background: 'var(--color-card)',
    color: '#fff',
    cursor: 'pointer',
    outline: 'none',
  },
  totals: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 11.5,
    fontFamily: "'DM Mono', monospace",
    color: 'rgba(255,255,255,0.7)',
    marginBottom: 10,
    flexWrap: 'wrap',
  },
  totalItem: { color: 'rgba(255,255,255,0.85)' },
  totalDivider: { color: 'rgba(255,255,255,0.25)', userSelect: 'none' },
  chipsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    alignItems: 'center',
    marginTop: 12,
    marginBottom: 12,
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 0,
    padding: '4px 10px',
    borderRadius: 0,
    border: '1px solid rgba(255,255,255,0.16)',
    background: 'var(--color-surface)',
    cursor: 'pointer',
    fontSize: 11,
    fontFamily: "'DM Mono', monospace",
    transition: 'opacity 0.15s',
  },
  chipHidden: {
    opacity: 0.4,
    borderStyle: 'dashed',
  },
  chipSwatch: {
    display: 'inline-block',
    width: 7,
    height: 7,
  },
  chipLabel: {
    color: '#fff',
    marginLeft: 5,
    maxWidth: 140,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  chipLabelHidden: {
    color: 'var(--color-label)',
    textDecoration: 'line-through',
  },
  chipHint: {
    fontSize: 10,
    fontFamily: "'DM Mono', monospace",
    color: 'var(--color-label)',
    marginLeft: 4,
  },
  empty: {
    textAlign: 'center',
    padding: '24px 0',
  },
  muted: {
    color: 'var(--color-label)',
    fontSize: 12,
    fontFamily: "'DM Mono', monospace",
  },
};

// Add media query styles for responsive behavior
const mobileStyles = `
@media (max-width: 768px) {
  .combined-hourly-graph .top-controls-row {
    gap: 6px;
  }
  .combined-hourly-graph .tab-group button {
    padding: 4px 8px;
    font-size: 11px;
  }
}
`;

// Inject media query styles if needed (optional)
if (typeof document !== 'undefined') {
  const styleSheet = document.createElement("style");
  styleSheet.textContent = mobileStyles;
  document.head.appendChild(styleSheet);
}

export default CombinedHourlyGraph;
